import React, { useState } from 'react';
import { Button, Badge, Logo, FadeIn, Field } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { pickImage, captureSelfie } from '../api/imagePicker';
import api from '../api/client';

// One document row: number field + photo upload.
function DocField({ label, placeholder, value, onChange, image, onPick, maxLength, inputMode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <Field label={label} placeholder={placeholder} value={value} onChange={onChange}
        maxLength={maxLength} inputMode={inputMode} />
      <button type="button" onClick={onPick} className="row" style={{
        width: '100%', gap: 12, border: '1px dashed var(--border)', borderRadius: 'var(--r-md)',
        padding: 12, background: 'var(--surface)', textAlign: 'left',
      }}>
        {image ? (
          <>
            <img src={image} alt="" style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover' }} />
            <span className="grow muted" style={{ fontSize: 13 }}>Photo attached · tap to change</span>
            <span style={{ color: 'var(--success)', fontWeight: 800 }}>✓</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 20 }}>📷</span>
            <span className="grow muted" style={{ fontSize: 13 }}>Upload {label} photo</span>
            <span style={{ color: 'var(--primary)', fontWeight: 700 }}>Add</span>
          </>
        )}
      </button>
    </div>
  );
}

export default function StaffOnboarding() {
  const { user, refreshUser, logout } = useAuth();
  const { toast } = useToast();
  const status = user?.kyc?.status;
  const [reapply, setReapply] = useState(false);
  const isPending = status === 'pending' && !reapply;
  const isRejected = status === 'rejected' && !reapply;

  const [name, setName] = useState(user?.name || '');
  const [aadhaar, setAadhaar] = useState('');
  const [aadhaarImg, setAadhaarImg] = useState(null);
  const [pan, setPan] = useState('');
  const [panImg, setPanImg] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  const pick = async (setter) => {
    const res = await pickImage();
    if (res?.dataUri) setter(res.dataUri);
  };

  const takeSelfie = async () => {
    const res = await captureSelfie();
    if (res?.dataUri) setSelfie(res.dataUri);
  };

  const submit = async () => {
    if (!name.trim()) return toast('Please enter your full name', { tone: 'error' });
    if (aadhaar.replace(/\s/g, '').length !== 12) return toast('Enter a valid 12-digit Aadhaar', { tone: 'error' });
    if (!/^[A-Za-z]{5}\d{4}[A-Za-z]$/.test(pan.trim())) return toast('Enter a valid PAN (ABCDE1234F)', { tone: 'error' });
    if (!aadhaarImg || !panImg) return toast('Please attach photos of both Aadhaar and PAN', { tone: 'error' });
    if (!selfie) return toast('Please capture a selfie for verification', { tone: 'error' });

    setLoading(true);
    try {
      // Save the name first, then run the third-party (IDSpay) verification on
      // the server, which stores the response for admin review.
      await api.put('/users/me', { name: name.trim() });
      await api.post('/users/kyc/submit', {
        aadhaarNumber: aadhaar.replace(/\s/g, ''),
        aadhaarImage: aadhaarImg,
        panNumber: pan.trim().toUpperCase(),
        panImage: panImg,
        selfieImage: selfie,
      });
      await refreshUser(); // pulls the new 'pending' status → review screen
      setReapply(false);
      toast("Your documents were verified and sent for admin approval. You'll get access once an admin approves.", {
        title: 'Submitted', tone: 'success', duration: 6000,
      });
    } catch (err) {
      toast(err.message || 'Please try again', { title: 'Could not submit', tone: 'error' });
    }
    setLoading(false);
  };

  const checkStatus = async () => {
    setChecking(true);
    const u = await refreshUser();
    setChecking(false);
    if (u?.kyc?.status !== 'approved') {
      toast("Your KYC hasn't been approved yet. Please check back soon.", { title: 'Still under review' });
    }
  };

  const statusScreen = (bg, emoji, title, body, actions) => (
    <div className="app-shell">
      <div className="screen screen--plain" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, minHeight: '100vh',
      }}>
        <FadeIn style={{ textAlign: 'center', maxWidth: 440 }}>
          <div style={{
            width: 110, height: 110, borderRadius: '50%', background: bg, margin: '0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 46,
          }}>{emoji}</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginTop: 20 }}>{title}</h1>
          {body}
          {actions}
          <button onClick={logout} className="muted" style={{ marginTop: 16, fontWeight: 600 }}>Logout</button>
        </FadeIn>
      </div>
    </div>
  );

  // ---- Under review (pending) ----
  if (isPending) {
    return statusScreen('#FCF1DA', '⏳', 'Admin will verify your details',
      <>
        <p className="muted" style={{ fontSize: 14.5, marginTop: 10, lineHeight: 1.5 }}>
          Your Aadhaar &amp; PAN have been submitted. Our admin team is reviewing them — you'll get access once
          approved, and we'll notify you on WhatsApp.
        </p>
        <div style={{ marginTop: 16 }}><Badge label="Status: Pending admin approval" tone="warning" /></div>
        {user?.kyc?.attemptCount > 1 && (
          <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>Application attempt #{user.kyc.attemptCount}</p>
        )}
      </>,
      <Button title={checking ? 'Checking…' : 'Check approval status'} onClick={checkStatus} disabled={checking}
        style={{ marginTop: 26, maxWidth: 240, marginLeft: 'auto', marginRight: 'auto' }} />
    );
  }

  // ---- Rejected (admin's reason + re-apply) ----
  if (isRejected) {
    return statusScreen('#FBE6E9', '❌', 'Verification rejected',
      <div className="card" style={{ marginTop: 18, textAlign: 'left' }}>
        <div className="field__label">Reason from admin</div>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          {user?.kyc?.rejectionReason || 'Your documents could not be verified. Please re-apply with correct details.'}
        </p>
      </div>,
      <Button title="Re-apply for Verification" onClick={() => setReapply(true)}
        style={{ marginTop: 24, maxWidth: 260, marginLeft: 'auto', marginRight: 'auto' }} />
    );
  }

  // ---- KYC form ----
  return (
    <div className="app-shell">
      <div className="screen screen--plain" style={{ padding: 20, paddingTop: 'calc(var(--sat) + 20px)', maxWidth: 560, margin: '0 auto', width: '100%', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><Logo size="md" /></div>
        <h1 style={{ fontSize: 24, fontWeight: 800, textAlign: 'center' }}>Verify your identity</h1>
        <p className="muted center" style={{ marginBottom: 20, lineHeight: 1.5 }}>
          Complete KYC to start receiving jobs. Verified via IDSpay &amp; approved by our team.
        </p>

        {loading && (
          <div className="overlay-busy" style={{ position: 'fixed', zIndex: 150 }}>
            <div className="spinner" />
            <span style={{ fontWeight: 700 }}>Verifying with IDSpay…</span>
            <span className="muted" style={{ fontSize: 12.5 }}>Checking your Aadhaar &amp; PAN</span>
          </div>
        )}

        <div className="card">
          <Field label="Full name (as per Aadhaar)" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />

          <DocField
            label="Aadhaar Number" placeholder="XXXX XXXX XXXX" value={aadhaar} maxLength={14} inputMode="numeric"
            onChange={(e) => {
              const d = e.target.value.replace(/\D/g, '').slice(0, 12);
              setAadhaar(d.replace(/(.{4})/g, '$1 ').trim());
            }}
            image={aadhaarImg} onPick={() => pick(setAadhaarImg)}
          />

          <DocField
            label="PAN Number" placeholder="ABCDE1234F" value={pan} maxLength={10}
            onChange={(e) => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
            image={panImg} onPick={() => pick(setPanImg)}
          />

          <div className="field__label">Live Selfie</div>
          <button type="button" onClick={takeSelfie} className="row" style={{
            width: '100%', gap: 12, border: '1px dashed var(--border)', borderRadius: 'var(--r-md)',
            padding: 12, textAlign: 'left',
          }}>
            <span className="avatar" style={{ width: 56, height: 56, background: 'var(--bg)', fontSize: 26 }}>
              {selfie ? <img src={selfie} alt="" /> : '🤳'}
            </span>
            <span className="grow">
              <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>
                {selfie ? 'Selfie captured' : 'Take a selfie'}
              </span>
              <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                Used by admin to match your face with your documents.
              </span>
            </span>
            <span style={{ color: selfie ? 'var(--success)' : 'var(--primary)', fontWeight: 800 }}>{selfie ? '✓' : 'Open'}</span>
          </button>

          <Button title={loading ? 'Submitting…' : 'Submit for Verification'} onClick={submit} disabled={loading} style={{ marginTop: 18 }} />
        </div>

        <div className="card card--tight center" style={{ marginTop: 16 }}>
          <span className="muted" style={{ fontSize: 12.5 }}>
            🔒 Your documents are encrypted and used only for verification.
          </span>
        </div>

        <div className="center">
          <button onClick={logout} className="muted" style={{ marginTop: 18, fontWeight: 600 }}>Logout</button>
        </div>
      </div>
    </div>
  );
}
